import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { todosAPI } from '../services/api';
import { TaskStatus } from '../types';
import type { Todo, TodoUpdate } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface UseTodosOptions {
  search?: string;
  status?: TaskStatus[];
  tags?: string[];
  team_id?: string | null;
  include_personal?: boolean;
  parent_only?: boolean;
  child_only?: boolean;
}

export const useTodos = (options: UseTodosOptions = {}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Include user ID in query key to ensure data isolation between users
  const baseQueryKey = ['todos', { userId: user?.id }];

  // Fetch todos - include server-side filters in query key
  const {
    data: todos = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: [...baseQueryKey, { 
      parent_only: options.parent_only,
      child_only: options.child_only,
      team_id: options.team_id,
      include_personal: options.include_personal,
      search: options.search || undefined,
      status: options.status,
      tags: options.tags,
    }],
    queryFn: () => todosAPI.getAll({
      parent_only: options.parent_only,
      child_only: options.child_only,
      team_id: options.team_id || undefined,
      include_personal: options.include_personal,
      search: options.search || undefined,
      status: options.status,
      tags: options.tags,
    }),
    staleTime: 5 * 60 * 1000, // 5 minutes - prevent refetching
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!user, // Only fetch when user is authenticated
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount
  });

  // Server-side filtering is now primary, no need for additional client-side filtering
  const filteredTodos = todos;

  // Create todo mutation
  const createTodoMutation = useMutation({
    mutationFn: todosAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', { userId: user?.id }] });
    },
    onError: (error) => {
      console.error('Failed to create todo:', error);
    }
  });

  // Update todo mutation
  const updateTodoMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Todo> }) => {
      // Ensure we only send defined fields to avoid sending undefined as None
      // Note: We need to preserve false values
      const cleanData = Object.fromEntries(
        Object.entries(data).filter((entry) => entry[1] !== undefined)
      ) as TodoUpdate;
      return todosAPI.update(id, cleanData);
    },
    onMutate: async ({ id, data }) => {
      // Optimistic update - cancel outgoing queries for this user
      await queryClient.cancelQueries({ queryKey: ['todos', { userId: user?.id }] });
      
      // Get all todo cache entries for this user and update them
      const cacheEntries = queryClient.getQueriesData({ queryKey: ['todos', { userId: user?.id }] });
      const previousData = new Map();
      
      cacheEntries.forEach(([queryKey, queryData]) => {
        if (Array.isArray(queryData)) {
          previousData.set(queryKey, queryData);
          // Optimistically update each cache entry
          const updatedData = queryData.map(todo => todo.id === id ? { ...todo, ...data } : todo);
          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      return { previousData };
    },
    onSuccess: () => {
      // Successfully updated
    },
    onError: (err, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      console.error('Failed to update todo:', err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', { userId: user?.id }] });
      queryClient.invalidateQueries({ queryKey: ['weeks'] });
      queryClient.invalidateQueries({ queryKey: ['week-tasks'] });
    }
  });

  // Delete todo mutation
  const deleteTodoMutation = useMutation({
    mutationFn: todosAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos', { userId: user?.id }] });
    },
    onError: (err) => {
      console.error('Failed to delete todo:', err);
    }
  });

  // Memoize unique tags computation — sorted by frequency among active (non-completed/canceled) todos
  const allTags = useMemo(
    () => {
      const activeTodos = todos.filter(t => t.status !== TaskStatus.COMPLETED && t.status !== TaskStatus.CANCELED);
      const tagCounts = new Map<string, number>();
      for (const todo of activeTodos) {
        for (const tag of todo.tags) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      }
      return Array.from(tagCounts.entries())
        .sort((a, b) => b[1] - a[1]) // most frequent first
        .map(([tag]) => tag);
    },
    [todos]
  );

  // Memoize statistics computation
  const stats = useMemo(
    () => ({
      total: todos.length,
      completed: todos.filter(t => t.status === TaskStatus.COMPLETED).length,
      inProgress: todos.filter(t => t.status === TaskStatus.IN_PROGRESS).length,
      nowTasks: todos.filter(t => t.horizon === 'now').length,
    }),
    [todos]
  );

  return {
    todos: filteredTodos,
    allTodos: todos,
    allTags,
    stats,
    isLoading,
    error,
    refetch,
    createTodo: createTodoMutation.mutate,
    updateTodo: updateTodoMutation.mutate,
    deleteTodo: deleteTodoMutation.mutate,
    isCreating: createTodoMutation.isPending,
    isUpdating: updateTodoMutation.isPending,
    isDeleting: deleteTodoMutation.isPending,
  };
}; 